<template>
  <div class="my-tree-container" :class="className">
    <MyTree
      ref="myTree"
      @addField="ADD_FIELD"
      @drag="dragging = true"
      @drop="dragging = false"
      @moving="onDragMoving"
      :ondragend="onDragEnd"
      :data="value.fields"
      :scope="scope"
      :usedKeys="value.usedKeys"
      draggable
      :crossTree="false"
      :space="0"
      :indent="30"
      :getTriggerEl="dragHandler">

      <div slot-scope="{data, store, scope}">
        <template v-if="!data.isDragPlaceHolder">
          <Field :field="data" :scope="scope" :usedKeys="value.usedKeys" @onDelete="openConfirmDeleteModal">
            <button
              slot="toggleBtn"
              v-if="showToggleBtn(data)"
              @click="store.toggleOpen(data)"
              class="field__toggle-btn"
              :class="{ 'field__toggle-btn--opened': data.open }"
              >
              <BaseIcon
                name="arrow"
              ></BaseIcon>
            </button>
          </Field>
        </template>
      </div>

    </MyTree>
  </div>
</template>

<script>
import {DraggableTree} from 'vue-draggable-nested-tree'
import MyTree from '@/components/MyTree'
import Field from '@/components/Field'
import { mapFields } from '@/helpers'
import { MDSModalConfirmDecisionModel } from '@/models/modal-datasets.model'
import { SchemaFieldModel } from '@/models/schema-field.model'
import SchemaChangeDetector from '@/plugins/schema-change-detector'

export default {
  components: {Tree: DraggableTree, Field, MyTree},
  props: {
    value: Object,
    scope: String,
    customNamespace: String
  },
  data () {
    return {
      dragging: false,
      blockDrop: false,
      changeDetector: new SchemaChangeDetector()
    }
  },
  computed: {
    className () {
      return {
        'my-tree-container--dragging': this.dragging,
        'my-tree-container--drop-blocked': this.blockDrop,
      }
    }
  },
  methods: {
    showToggleBtn (data) {
      if (data.type !== 'repeater' && data.type !== 'group' && data.children.length === 1 && data.children[0].isDragPlaceHolder) {
        return false
      } else {
        return data.children && data.children.length || data.type === 'repeater' || data.type === 'group'
      }
    },
    draggableRulesTest ({ draggingField, destinationNode, trueCallback, falseCallback }) {
      if (destinationNode.parent && destinationNode.parent.type === 'repeater') {
        // console.warn("🎟 You can't nest repeater fields")
        return falseCallback()
      }
      
      else if (draggingField.parent.type === 'repeater' && destinationNode.type !== 'repeater') {
        // console.warn("🎟 You can't drag field out of repeater")
        return falseCallback()
      }

      else if (destinationNode.type === 'repeater' && draggingField.parent.type !== 'repeater') {
        // console.warn("🎟 You can't drag field into repeater children")
        return falseCallback()
      }

      else if (destinationNode.type === 'repeater' && draggingField.parent.type === 'repeater' && destinationNode._id !== draggingField.parent._id) {
        // console.warn("🎟 You can't move foreign repeater field below another repeater")
        return falseCallback()
      }

      else if (destinationNode.type !== 'group' && destinationNode.type !== 'repeater' && !destinationNode.isRoot) {
        // console.warn("🎟 You can nest only under group type")
        return falseCallback()
      }

      else if (draggingField.type === 'group' && destinationNode.type === 'group') {
        // console.warn("🎟 You can't nest groups")
        return falseCallback()
      }

      else if (trueCallback) {
        trueCallback()
      }

      return true
    },
    dragHandler (nodeVm) {
      return nodeVm.$el.firstChild.querySelector('.js-drag-handle')
    },
    onDragStart (node, nodeVm, store, event) {
      this.dragging = true
    },
    onDragMoving (node, nodeVm, store, event) {
      const draggingField = node
      const destinationNode = this.$refs.myTree.dplh.parent

      this.draggableRulesTest({
        draggingField,
        destinationNode,
        trueCallback: () => {
          this.blockDrop = false
        },
        falseCallback: () => {
          this.blockDrop = true
        }
      })
    },
    onDragEnd (node, nodeVm, store, event) {
      this.dragging = false

      const draggingField = node
      const destinationNode = this.$refs.myTree.dplh.parent

      const isDragPossible =  this.draggableRulesTest({
        draggingField,
        destinationNode,
        falseCallback: () => {
          return false
        }
      })

      if (isDragPossible) {
        this.changeDetector.registerChange({ name: 'TreeDragging', bool: true })
      }

      return isDragPossible
    },
    ADD_FIELD ({ field, parent }) {

        // --> Check if key exists
        if (this.value.usedKeys.indexOf(field.key) > -1) {
            console.error(`Key ${field.key} already used`)
            return
        }

        // --> Add key to used keys
        this.value.usedKeys.push(field.key)

        // --> Add field to schema
        if (parent) {
            // -> Add children
            if (!parent.children) {
                parent.children = []
            }

            parent.children.push(field)
        } else {
            // -> Add root
            this.value.fields.push(field)
        }

        this.value.fields = mapFields(this.value.fields)
        this.changeDetector.checkUsedKeys(this.value.usedKeys)
    },
    PATCH_FIELD ({ editedField, form }) {
      const keyIndex = this.value.usedKeys.indexOf(editedField.key)
      this.value.usedKeys[keyIndex] = form.key

      this.changeDetector.lastSavedField = editedField

      const _newData = new SchemaFieldModel({
        ...form,
        subForm: { ...form.subForm }
      })
      // Check before Object.assign(editedField, form)
      this.changeDetector.checkFieldData(_newData)

      Object.assign(editedField, form)
      
      this.changeDetector.checkUsedKeys(this.value.usedKeys)
    },
    REMOVE_FIELD({ field, parent }) {

        // --> Get indexes
        let fieldIndex = (parent) ? parent.children.indexOf(field) : this.value.fields.indexOf(field)

        // --> Remove field from schema
        if (parent) {
            // -> Remove children
            parent.children.splice(fieldIndex, 1)
        } else {
            // -> Remove root
            this.value.fields.splice(fieldIndex, 1)
        }

        // --> remove children keys froms schema
        if (field.children && field.children.length) {
          let childKeyIndex
          for (let child of field.children) {
            childKeyIndex = this.value.usedKeys.indexOf(child.key)

            if (childKeyIndex > -1) {
              this.value.usedKeys.splice(childKeyIndex, 1)
            }
          }
        }

        // --> remove key from used
        let keyIndex = this.value.usedKeys.indexOf(field.key)
        this.value.usedKeys.splice(keyIndex, 1)
        this.changeDetector.checkUsedKeys(this.value.usedKeys)
    },
    openConfirmDeleteModal (payload) {
      const props = new MDSModalConfirmDecisionModel({
        confirmationHandler: () => this.REMOVE_FIELD(payload)
      })
      this.$store.commit('modals/OPEN_MODAL', { name: 'ModalConfirmDecision', props })
    },
    afterSaveHandler () {
      this.changeDetector.lastSavedUsedKeys = this.value.usedKeys
      this.changeDetector.resetLastSavedFields()
      this.changeDetector.resetRegisteredChanges()
    }
  },
  created () {
    this.$eventBus.$on('ADD_NEW_FIELD', this.ADD_FIELD)
    this.$eventBus.$on('ModalNewField/ADD_NEW_FIELD', this.ADD_FIELD)
    this.$eventBus.$on('ModalNewField/PATCH_FIELD', this.PATCH_FIELD)
    this.changeDetector.lastSavedUsedKeys = this.value.usedKeys
    this.$eventBus.$on('AFTER_SAVE_SCHEMA', this.afterSaveHandler)
  },
  beforeDestroy () {
    this.$eventBus.$off('ADD_NEW_FIELD', this.ADD_FIELD)
    this.$eventBus.$off('ModalNewField/ADD_NEW_FIELD', this.ADD_FIELD)
    this.$eventBus.$off('ModalNewField/PATCH_FIELD', this.PATCH_FIELD)
    this.$eventBus.$off('AFTER_SAVE_SCHEMA', this.afterSaveHandler)
  }
}
</script>

<style lang="scss">
  @import './src/assets/styles/colors.scss';

  .my-tree-container {
    background-color: $color-catskill-white;
    border-radius: 3px;
    margin-top: -1px;
    // overflow: hidden; if turned on -> draggable-placeholder is stripped /else border-radius is missing
    box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);

    .draggable-placeholder-inner {
      position: relative;
      height: 0 !important;

      &:before, &:after {
        content: '';
        position: absolute;
        z-index: 10;
        transition-duration: .15s;
      }

      &:before {
        top: -1px;
        right: 0;
        bottom: -1px;
        left: 0;
        background-color: $color-purple;
        transition-property: background-color;
      }

      &:after {
        top: -7px;
        left: -2px;
        width: 10px;
        height: 10px;
        background-color: #fff;
        border: 2px solid $color-purple;
        border-radius: 50%;
        transition-property: border-color;
      }
    }

    .tree-node.dragging {
      width: auto !important;
      opacity: .85 !important;
      border-radius: 6px;

      .tree-node-children {
        display: none !important;
      }

      .field {
        padding-right: 20px;
        
        .ghost__counter {
          visibility: visible;
        }

        > *:not(.field-dragging-ghost) {
          display: none !important;
        }
      }
    }

    &--dragging {
      user-select: none;

      .new-field-button:hover {
        background-color: inherit !important;
        color: inherit !important;
      }
    }

    &--drop-blocked {
      .draggable-placeholder-inner {
        &:before {
          background-color: $color-monza;
        }
        &:after {
          border-color: $color-monza;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/SchemaTreeContainer.vue